// Full Updated File: pages/admin/AdminPromos.jsx
// Fixes: Use getStats for edit, cleanFormData syntax, use item.name if needed (not here)

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Percent, Coins, Calendar, Check, X, Loader2 } from 'lucide-react';
import { promosAPI } from '../../services/api';
import './css/AdminPromos.css';

const AdminPromos = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    type: 'flat',
    value: '',
    max_discount: '',
    min_cart_value: '',
    total_uses: '',
    per_user_uses: 1,
    expiration_date: '',
    is_active: true,
    scope_type: 'all',
    scope_ids: []
  });
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  const getTypeLabel = (type) => (type === 'flat' ? 'MONTANT' : 'POURCENTAGE');

  const getScopeLabel = (scopeType) => {
    switch (scopeType) {
      case 'all':
        return 'Tous les articles';
      case 'category':
        return 'Categories specifiques';
      case 'product':
        return 'Produits specifiques';
      case 'exclude_category':
        return 'Exclure categories';
      case 'exclude_product':
        return 'Exclure produits';
      default:
        return scopeType;
    }
  };

  useEffect(() => {
    fetchPromos();
  }, []);

  const fetchPromos = async () => {
    try {
      const response = await promosAPI.getAll();
      setPromos(response.data);
    } catch (err) {
      console.error('Failed to fetch promos');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleScopeIdsChange = (e) => {
    const ids = e.target.value.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id) && id > 0);
    setFormData(prev => ({ ...prev, scope_ids: ids }));
  };

  const cleanFormData = (data) => {
    const cleaned = { ...data };
    // Convert empty strings to null for optional fields
    if (cleaned.max_discount === '') cleaned.max_discount = null;
    if (cleaned.min_cart_value === '') cleaned.min_cart_value = null;
    if (cleaned.total_uses === '') cleaned.total_uses = null;
    if (cleaned.expiration_date === '') cleaned.expiration_date = null;
    // Ensure scope_ids is array
    if (!Array.isArray(cleaned.scope_ids)) cleaned.scope_ids = [];
    return cleaned;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanedData = cleanFormData(formData);
    try {
      if (editingPromo) {
        await promosAPI.update(editingPromo.id, cleanedData);
      } else {
        await promosAPI.create(cleanedData);
      }
      fetchPromos();
      closeModal();
    } catch (err) {
      console.error('Failed to save promo', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Confirmer ?')) return;
    try {
      await promosAPI.delete(id);
      fetchPromos();
    } catch (err) {
      console.error('Failed to delete promo');
    }
  };

  const handleEdit = async (promo) => {
    try {
      const response = await promosAPI.getStats(promo.id);
      const fullPromo = response.data;
      setEditingPromo(fullPromo);
      setFormData({
        code: fullPromo.code || '',
        type: fullPromo.type || 'flat',
        value: fullPromo.value || '',
        max_discount: fullPromo.max_discount || '',
        min_cart_value: fullPromo.min_cart_value || '',
        total_uses: fullPromo.total_uses || '',
        per_user_uses: fullPromo.per_user_uses || 1,
        expiration_date: fullPromo.expiration_date ? fullPromo.expiration_date.slice(0,16) : '',
        is_active: fullPromo.is_active || false,
        scope_type: fullPromo.scope_type || 'all',
        scope_ids: Array.isArray(fullPromo.scope_ids) ? fullPromo.scope_ids : []
      });
      setStats({
        total_used: fullPromo.total_used || 0,
        unique_users: fullPromo.unique_users || 0
      });
      setShowEditModal(true);
    } catch (err) {
      console.error('Failed to fetch promo stats');
    }
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setEditingPromo(null);
    setFormData({
      code: '',
      type: 'flat',
      value: '',
      max_discount: '',
      min_cart_value: '',
      total_uses: '',
      per_user_uses: 1,
      expiration_date: '',
      is_active: true,
      scope_type: 'all',
      scope_ids: []
    });
    setStats(null);
  };

  if (loading) {
    return (
      <div className="promo-admin-loading-container">
        <Loader2 className="promo-admin-loading-spinner" />
        <p>Chargement des codes promo...</p>
      </div>
    );
  }

  return (
    <div className="promo-admin-container">
      <div className="promo-admin-header">
        <div className="promo-admin-header-content">
          <h1 className="promo-admin-title">Gestion des codes promo</h1>
          <p className="promo-admin-subtitle">Gerer et creer des codes de reduction pour votre boutique</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="promo-admin-create-btn">
          <Plus size={20} className="promo-admin-icon" />
          Creer une promo
        </button>
      </div>

      <div className="promo-admin-table-table-wrapper">
        {promos.length === 0 ? (
          <div className="promo-admin-empty-state">
            <Calendar className="promo-admin-empty-icon" />
            <h3>Aucun code promo pour le moment</h3>
            <p>Creez votre premier code promo pour commencer.</p>
            <button onClick={() => setShowCreateModal(true)} className="promo-admin-empty-btn">
              Creer la premiere promo
            </button>
          </div>
        ) : (
          <div className="promo-admin-table-container">
            <table className="promo-admin-table">
              <thead className="promo-admin-thead">
                <tr>
                  <th className="promo-admin-th">Code</th>
                  <th className="promo-admin-th">Type</th>
                  <th className="promo-admin-th">Valeur</th>
                  <th className="promo-admin-th">Panier min</th>
                  <th className="promo-admin-th">Utilisations totales</th>
                  <th className="promo-admin-th">Par utilisateur</th>
                  <th className="promo-admin-th">Actif</th>
                  <th className="promo-admin-th">Portee</th>
                  <th className="promo-admin-th">Utilise</th>
                  <th className="promo-admin-th">Actions</th>
                </tr>
              </thead>
              <tbody className="promo-admin-tbody">
                {promos.map((promo) => (
                  <tr key={promo.id} className="promo-admin-tr">
                    <td className="promo-admin-td">{promo.code}</td>
                    <td className="promo-admin-td">
                      {promo.type === 'flat' ? <Coins size={16} /> : <Percent size={16} />}
                      {getTypeLabel(promo.type)}
                    </td>
                    <td className="promo-admin-td">
                      {promo.type === 'flat' ? `DT ${promo.value}` : `${promo.value}%`}
                      {promo.max_discount && ` (max DT ${promo.max_discount})`}
                    </td>
                    <td className="promo-admin-td">DT {promo.min_cart_value || 0}</td>
                    <td className="promo-admin-td">
                      {promo.total_uses ? `${promo.total_used || 0}/${promo.total_uses}` : 'Illimite'}
                    </td>
                    <td className="promo-admin-td">{promo.per_user_uses}</td>
                    <td className="promo-admin-td">
                      {promo.is_active ? <Check color="green" size={16} /> : <X color="red" size={16} />}
                    </td>
                    <td className="promo-admin-td">{getScopeLabel(promo.scope_type)}</td>
                    <td className="promo-admin-td">{promo.total_used || 0}</td>
                    <td className="promo-admin-td">
                      <button onClick={() => handleEdit(promo)} className="promo-admin-btn promo-admin-btn-small">
                        <Edit size={16} />
                      </button>
                      <button onClick={() => handleDelete(promo.id)} className="promo-admin-btn promo-admin-btn-danger-small">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="promo-admin-modal-overlay">
          <div className="promo-admin-modal">
            <div className="promo-admin-modal-header">
              <h2>{editingPromo ? 'Modifier la promo' : 'Creer une promo'}</h2>
              <button onClick={closeModal} className="promo-admin-modal-close">X</button>
            </div>
            <form onSubmit={handleSubmit} className="promo-admin-form">
              <div className="promo-admin-form-group">
                <label className="promo-admin-label">Code *</label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  placeholder="ex. BIENVENUE10"
                  className="promo-admin-input"
                  required
                />
              </div>
              <div className="promo-admin-form-group">
                <label className="promo-admin-label">Type *</label>
                <select name="type" value={formData.type} onChange={handleInputChange} className="promo-admin-select" required>
                  <option value="flat">Montant fixe</option>
                  <option value="percentage">Pourcentage</option>
                </select>
              </div>
              <div className="promo-admin-form-group">
                <label className="promo-admin-label">Valeur *</label>
                <input
                  type="number"
                  name="value"
                  value={formData.value}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  placeholder="ex. 10 pour 10 DT ou 20 pour 20%"
                  className="promo-admin-input"
                  required
                />
              </div>
              {formData.type === 'percentage' && (
                <div className="promo-admin-form-group">
                  <label className="promo-admin-label">Remise max (optionnel)</label>
                  <input
                    type="number"
                    name="max_discount"
                    value={formData.max_discount}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    placeholder="ex. 50"
                    className="promo-admin-input"
                  />
                </div>
              )}
              <div className="promo-admin-form-group">
                <label className="promo-admin-label">Valeur min du panier (optionnel)</label>
                <input
                  type="number"
                  name="min_cart_value"
                  value={formData.min_cart_value}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  placeholder="ex. 25"
                  className="promo-admin-input"
                />
              </div>
              <div className="promo-admin-form-group">
                <label className="promo-admin-label">Utilisations totales (optionnel - laisser vide pour illimite)</label>
                <input
                  type="number"
                  name="total_uses"
                  value={formData.total_uses}
                  onChange={handleInputChange}
                  min="0"
                  className="promo-admin-input"
                />
              </div>
              <div className="promo-admin-form-group">
                <label className="promo-admin-label">Utilisations par utilisateur *</label>
                <input
                  type="number"
                  name="per_user_uses"
                  value={formData.per_user_uses}
                  onChange={handleInputChange}
                  min="1"
                  className="promo-admin-input"
                  required
                />
              </div>
              <div className="promo-admin-form-group">
                <label className="promo-admin-label">Date d'expiration (optionnel)</label>
                <input
                  type="datetime-local"
                  name="expiration_date"
                  value={formData.expiration_date}
                  onChange={handleInputChange}
                  className="promo-admin-input"
                />
              </div>
              <div className="promo-admin-form-group promo-admin-checkbox-group">
                <label className="promo-admin-checkbox-label">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                  />
                  <span className="promo-admin-checkbox-fake"></span>
                  Actif
                </label>
              </div>
              <div className="promo-admin-form-group">
                <label className="promo-admin-label">Type de portee *</label>
                <select name="scope_type" value={formData.scope_type} onChange={handleInputChange} className="promo-admin-select" required>
                  <option value="all">Tous les articles</option>
                  <option value="category">Categories specifiques</option>
                  <option value="product">Produits specifiques</option>
                  <option value="exclude_category">Exclure categories</option>
                  <option value="exclude_product">Exclure produits</option>
                </select>
              </div>
              {(formData.scope_type !== 'all') && (
                <div className="promo-admin-form-group">
                  <label className="promo-admin-label">IDs de portee (separes par des virgules)</label>
                  <input
                    type="text"
                    placeholder="ex. 1,2,3"
                    onChange={handleScopeIdsChange}
                    value={formData.scope_ids.join(',')}
                    className="promo-admin-input"
                  />
                  <small className="promo-admin-small-text">
                    {formData.scope_type.includes('category') ? 'IDs de categorie' : 'IDs de produit'}
                  </small>
                </div>
              )}
              {stats && (
                <div className="promo-admin-stats-card">
                  <h3 className="promo-admin-stats-title">Statistiques d'utilisation</h3>
                  <div className="promo-admin-stats-grid">
                    <div className="promo-admin-stat-item">
                      <span className="promo-admin-stat-value">{stats.total_used}</span>
                      <span className="promo-admin-stat-label">Total utilise</span>
                    </div>
                    <div className="promo-admin-stat-item">
                      <span className="promo-admin-stat-value">{stats.unique_users}</span>
                      <span className="promo-admin-stat-label">Utilisateurs uniques</span>
                    </div>
                  </div>
                </div>
              )}
              <div className="promo-admin-form-actions">
                <button type="button" onClick={closeModal} className="promo-admin-btn promo-admin-btn-secondary">
                  Annuler
                </button>
                <button type="submit" className="promo-admin-btn promo-admin-btn-primary">
                  {editingPromo ? 'Mettre a jour la promo' : 'Creer la promo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPromos;
